import React, { useMemo, forwardRef } from 'react';
import { FormControl, InputLabel, FormHelperText } from '@mui/material';
import ReactSelect from 'react-select';
import { neutral } from '_lex/_foundations/phxColors';

export type ReviewWorkflowOption = {
  value: number;
  label: string;
};

export type ReviewWorkflowPickerProps = {
  options: ReviewWorkflowOption[];
  isLoading?: boolean;
  value: number | '' | null;
  onChange: (value: number | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  id?: string;
  sx?: Record<string, unknown>;
};

const ReviewWorkflowPicker = forwardRef<HTMLDivElement, ReviewWorkflowPickerProps>(
  (
    {
      options,
      isLoading = false,
      value,
      onChange,
      label,
      placeholder,
      disabled = false,
      required = false,
      error = false,
      helperText,
      id = 'review-workflow-picker',
      sx,
    },
    ref,
  ) => {
    const selectedValue = useMemo(() => {
      const idToMatch = value === '' ? null : value;
      return idToMatch != null ? (options.find((opt) => opt.value === idToMatch) ?? null) : null;
    }, [value, options]);

    // Custom styles for react-select (pattern from ModulePicker)
    const customStyles = useMemo(
      () => ({
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        menu: (base: any) => ({ ...base, zIndex: 9999 }),
        input: (base: any) => ({
          ...base,
          boxShadow: 'none',
          outline: 0,
          minHeight: 45,
        }),
        control: (base: any, state: any) => ({
          ...base,
          backgroundColor: disabled ? neutral[100] : base.backgroundColor,
          borderColor: disabled ? neutral[300] : base.borderColor,
          color: disabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
          cursor: disabled ? 'not-allowed' : base.cursor,
          '&:hover': {
            borderColor: disabled ? neutral[300] : base['&:hover']?.borderColor,
          },
        }),
        singleValue: (base: any) => ({
          ...base,
          color: disabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
        }),
        placeholder: (base: any) => ({
          ...base,
          color: disabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
        }),
      }),
      [disabled],
    );

    return (
      <FormControl
        fullWidth
        required={required}
        error={error}
        disabled={disabled}
        className={error ? 'Mui-error' : ''}
        sx={sx}
        ref={ref}
      >
        {!!label && (
          <InputLabel
            id={`${id}-label`}
            required={required}
          >
            {label}
          </InputLabel>
        )}
        <ReactSelect<ReviewWorkflowOption>
          name={id}
          inputId={id}
          aria-labelledby={label ? `${id}-label` : undefined}
          value={selectedValue}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          styles={customStyles}
          classNamePrefix='react-select'
          menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
          onChange={(newValue) => {
            onChange(newValue?.value ?? null);
          }}
        />
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  },
);

ReviewWorkflowPicker.displayName = 'ReviewWorkflowPicker';

export default ReviewWorkflowPicker;
